import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ScrollText, Loader2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, RefreshCw, Filter,
} from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

const ACTION_LABELS = {
  block_account: 'Blocage de compte',
  unblock_account: 'Déblocage de compte',
  change_password: 'Changement de mot de passe',
  update_card_limits: 'Modification des plafonds carte',
  send_message: 'Envoi de message client',
  create_user: 'Création de compte',
  update_user: 'Modification de compte',
  delete_user: 'Suppression de compte',
  create_card: 'Création de carte',
  update_card: 'Modification de carte',
  delete_card: 'Suppression de carte',
  generate_iban: 'Génération d\'IBAN',
  generate_activation_code: 'Génération de code d\'activation',
  generate_upgrade_code: 'Génération de code d\'upgrade',
  update_balance: 'Mise à jour de solde',
  add_transaction: 'Ajout de transaction',
  delete_transaction: 'Suppression de transaction',
  update_status: 'Mise à jour de statut',
  validate_transfer: 'Validation de virement',
  cancel_transfer: 'Annulation de virement',
  block_transfer: 'Blocage de virement',
  delete_chatbot_history: 'Suppression de conversation chatbot',
  create_custom_response: 'Création de réponse personnalisée',
  update_custom_response: 'Modification de réponse personnalisée',
  delete_custom_response: 'Suppression de réponse personnalisée',
};

const TARGET_LABELS = {
  user: 'Utilisateur',
  card: 'Carte',
  transfer: 'Virement',
  transaction: 'Transaction',
  ticket: 'Ticket',
  chatbot: 'Chatbot',
  custom_response: 'Réponse perso',
  settings: 'Paramètres',
  system: 'Système',
};

function labelAction(a) {
  if (!a) return '—';
  return ACTION_LABELS[a] || a.replace(/_/g, ' ');
}
function labelTarget(tg) {
  if (!tg) return '—';
  return TARGET_LABELS[tg] || tg;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('fr-FR'); } catch (e) { return '—'; }
}

export default function AdminAuditLog() {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [filters, setFilters] = useState({ action: 'all', target_type: 'all', from: '', to: '' });
  const [expanded, setExpanded] = useState(null);
  const [actionOptions, setActionOptions] = useState([]);
  const [targetOptions, setTargetOptions] = useState([]);

  const load = async (toPage = page) => {
    try {
      setLoading(true);
      const params = { page: toPage, limit };
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.target_type !== 'all') params.target_type = filters.target_type;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const r = await api.get('/admin/audit-logs', { params });
      const data = r.data || {};
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      setPage(data.page || toPage);

      // collect unique action / target options from results (also persisted)
      setActionOptions((prev) => {
        const set = new Set(prev);
        (data.logs || []).forEach((l) => l.action && set.add(l.action));
        return Array.from(set).sort();
      });
      setTargetOptions((prev) => {
        const set = new Set(prev);
        (data.logs || []).forEach((l) => l.target_type && set.add(l.target_type));
        return Array.from(set).sort();
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Initial load: also fetch stats to populate dropdowns with known actions
  useEffect(() => {
    (async () => {
      try {
        const s = await api.get('/admin/audit-logs/stats');
        const sd = s.data || {};
        const acts = sd.actions || sd.by_action || {};
        const tgts = sd.targets || sd.by_target_type || {};
        const aKeys = Array.isArray(acts) ? acts.map((x) => x.action || x._id || x.key).filter(Boolean) : Object.keys(acts);
        const tKeys = Array.isArray(tgts) ? tgts.map((x) => x.target_type || x._id || x.key).filter(Boolean) : Object.keys(tgts);
        if (aKeys.length) setActionOptions((prev) => Array.from(new Set([...prev, ...aKeys])).sort());
        if (tKeys.length) setTargetOptions((prev) => Array.from(new Set([...prev, ...tKeys])).sort());
      } catch (e) { /* not fatal */ }
      load(1);
    })();
    // eslint-disable-next-line
  }, []);

  const applyFilters = () => { setPage(1); load(1); };
  const resetFilters = () => {
    setFilters({ action: 'all', target_type: 'all', from: '', to: '' });
    setPage(1);
    setTimeout(() => load(1), 0);
  };

  const go = (next) => {
    if (next < 1 || next > pages) return;
    load(next);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
            <ScrollText className="w-7 h-7 text-blue-700 dark:text-blue-400" /> {t('admin.auditTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('admin.auditSubtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={() => load(page)} data-testid="btn-audit-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> {t('common.update')}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 grid md:grid-cols-5 gap-3">
        <div className="md:col-span-1">
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Action</Label>
          <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
            <SelectTrigger data-testid="select-audit-action"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Toutes</SelectItem>
              {actionOptions.map((a) => (
                <SelectItem key={a} value={a}>{labelAction(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Cible</Label>
          <Select value={filters.target_type} onValueChange={(v) => setFilters({ ...filters, target_type: v })}>
            <SelectTrigger data-testid="select-audit-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {targetOptions.map((tg) => (
                <SelectItem key={tg} value={tg}>{labelTarget(tg)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Du</Label>
          <Input
            type="date"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            data-testid="input-audit-from"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Au</Label>
          <Input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            data-testid="input-audit-to"
          />
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={applyFilters} className="bg-blue-700 hover:bg-blue-800 flex-1" data-testid="btn-audit-apply">
            <Filter className="w-4 h-4 mr-1" /> Filtrer
          </Button>
          <Button variant="outline" onClick={resetFilters} data-testid="btn-audit-reset">Reset</Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Chargement...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400 text-sm">
            Aucune entrée pour ces filtres.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase text-slate-600 dark:text-slate-300">
                <tr>
                  <th className="text-left p-3">Date / heure</th>
                  <th className="text-left p-3">Admin</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Cible</th>
                  <th className="text-left p-3">Détails</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-800">
                {logs.map((l) => {
                  const id = l.id || l._id;
                  const isOpen = expanded === id;
                  return (
                    <React.Fragment key={id}>
                      <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <td className="p-3 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                        <td className="p-3 text-sm dark:text-slate-200">{l.admin_email || l.admin_name || l.admin_id || '—'}</td>
                        <td className="p-3 text-sm dark:text-slate-200">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                            {labelAction(l.action)}
                          </span>
                        </td>
                        <td className="p-3 text-sm dark:text-slate-200">
                          {l.target_type && (
                            <span className="text-xs text-slate-600 dark:text-slate-300">
                              {labelTarget(l.target_type)}
                            </span>
                          )}
                          {l.target_id && (
                            <div className="text-xs text-slate-400 dark:text-slate-500 font-mono">{String(l.target_id).slice(0, 12)}</div>
                          )}
                        </td>
                        <td className="p-3 text-xs text-slate-600 dark:text-slate-400 max-w-[260px] truncate">
                          {l.description || (l.metadata && JSON.stringify(l.metadata).slice(0, 80)) || '—'}
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setExpanded(isOpen ? null : id)}
                            data-testid={`btn-audit-expand-${id}`}
                          >
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60 dark:bg-slate-800/30">
                          <td colSpan={6} className="p-4">
                            <div className="grid md:grid-cols-2 gap-4 text-xs">
                              <div>
                                <div className="font-semibold text-slate-700 dark:text-slate-200 mb-1">Description</div>
                                <p className="text-slate-600 dark:text-slate-300 whitespace-pre-line">{l.description || '—'}</p>
                              </div>
                              <div>
                                <div className="font-semibold text-slate-700 dark:text-slate-200 mb-1">Métadonnées</div>
                                <pre className="bg-slate-900 text-slate-100 dark:bg-slate-950 rounded p-2 overflow-auto max-h-48">
{JSON.stringify(l.metadata || l.details || {}, null, 2)}
                                </pre>
                              </div>
                              <div className="md:col-span-2 text-slate-500 dark:text-slate-400">
                                IP : <span className="font-mono">{l.ip_address || l.ip || '—'}</span>
                                {' · '}
                                User agent : <span className="font-mono">{l.user_agent || '—'}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between p-3 border-t dark:border-slate-800 text-sm">
          <div className="text-slate-500 dark:text-slate-400">
            Page <strong className="dark:text-white">{page}</strong> / {pages} · {total} entrées
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => go(page - 1)}
              disabled={page <= 1 || loading}
              data-testid="btn-audit-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => go(page + 1)}
              disabled={page >= pages || loading}
              data-testid="btn-audit-next"
            >
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
